import { toast } from '@heroui/react';
import { useState, useMemo } from 'react';

import type { SortingState, PaginationState } from '@hs/ui';
import { AlertDialog, Button, Chip, Dropdown, Label } from '@heroui/react';
import {
  IconPlus as Plus,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { AiInvokeLogEditorModal } from './AiInvokeLogEditorModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import {
  AI_BILLING_CURRENCY_LABELS,
  AI_BILLING_DIMENSION_LABELS,
  AI_INVOKE_STATUS_LABELS,
  type AiBillingCurrency,
  type AiBillingDimension,
  type AiInvokeStatus,
} from '../constants';
import type { InvokeUsageItemFormValues } from './AiInvokeUsageItemsEditor';

type AiInvokeLogSpaceRef = {
  id?: string;
  name?: string | null;
};

type AiInvokeLogUserRef = {
  id?: string;
  username?: string | null;
  nickname?: string | null;
};

type AiInvokeLogProviderRef = {
  id?: string;
  name?: string | null;
  code?: string | null;
};

type AiInvokeLogModelRef = {
  id?: string;
  modelName?: string | null;
  modelCode?: string | null;
  modelVersion?: string | null;
};

type AiInvokeLogRuleRef = {
  id?: string;
  modelId?: string | null;
  version?: number | null;
  currency?: string | null;
};

type AiInvokeLog = {
  id: string;
  spaceId?: string | null;
  userId?: string | null;
  providerId?: string | null;
  modelId?: string | null;
  ruleId?: string | null;
  requestId?: string | null;
  usageItems?: InvokeUsageItemFormValues[] | null;
  amount?: number | null;
  creditCost?: number | null;
  status?: string | null;
  createdAt?: string | null;
  space?: AiInvokeLogSpaceRef | null;
  user?: AiInvokeLogUserRef | null;
  provider?: AiInvokeLogProviderRef | null;
  model?: AiInvokeLogModelRef | null;
  rule?: AiInvokeLogRuleRef | null;
};

function formatSpaceLabel(row: AiInvokeLog): string {
  if (row.space?.name) return row.space.name;
  if (row.spaceId) return row.spaceId;
  return '—';
}

function formatUserLabel(row: AiInvokeLog): string {
  const user = row.user;
  if (user?.nickname || user?.username) return user.nickname || user.username || '—';
  if (row.userId) return row.userId;
  return '—';
}

function formatProviderLabel(row: AiInvokeLog): string {
  const provider = row.provider;
  if (provider?.name || provider?.code) return provider.name || provider.code || '—';
  if (row.providerId) return row.providerId;
  return '—';
}

function formatModelLabel(row: AiInvokeLog): string {
  const model = row.model;
  if (model) {
    const name = model.modelName || model.modelCode || row.modelId || '—';
    const ver = model.modelVersion ? ` (${model.modelVersion})` : '';
    return `${name}${ver}`;
  }
  if (row.modelId) return row.modelId;
  return '—';
}

function formatRuleLabel(row: AiInvokeLog): string {
  const rule = row.rule;
  if (!rule && !row.ruleId) return '—';
  const modelLabel = formatModelLabel(row);
  const ver = rule?.version != null ? ` v${rule.version}` : '';
  const currency = rule?.currency
    ? ` · ${AI_BILLING_CURRENCY_LABELS[rule.currency as AiBillingCurrency] ?? rule.currency}`
    : '';
  if (rule) return `${modelLabel}${ver}${currency}`;
  return row.ruleId ?? '—';
}

function UsageItemsGrid({ items }: { items: InvokeUsageItemFormValues[] }) {
  if (!items.length) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="min-w-[28rem] py-1">
      <div className="grid grid-cols-4 gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>计价维度</span>
        <span>使用数量</span>
        <span>单价快照</span>
        <span>小计</span>
      </div>
      {items.map((item, index) => (
        <div
          key={`${item.usage}-${index}`}
          className="grid grid-cols-4 gap-x-3 gap-y-1 text-sm"
        >
          <span>{AI_BILLING_DIMENSION_LABELS[item.usage as AiBillingDimension] ?? item.usage}</span>
          <span>{item.quantity}</span>
          <span>{item.unit_price}</span>
          <span>{item.amount}</span>
        </div>
      ))}
    </div>
  );
}

function normalizeUsageItemsForGrid(raw: unknown): InvokeUsageItemFormValues[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const row = item as Record<string, unknown>;
    return {
      usage: String(row.usage ?? '') as InvokeUsageItemFormValues['usage'],
      quantity: Number(row.quantity ?? 0),
      unit_price: Number(row.unit_price ?? row.unitPrice ?? 0),
      amount: Number(row.amount ?? 0),
    };
  });
}

export function AiInvokeLogsManagement() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<AiInvokeLog | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<AiInvokeLog | null>(null);
  const [deletingLogId, setDeletingLogId] = useState<string | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];

  const { data, isLoading, refetch } = trpc.aiInvokeLog.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortItem
        ? { sortBy: sortItem.id, sortOrder: sortItem.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const logs = (data?.items ?? []) as AiInvokeLog[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteLogMutation = trpc.aiInvokeLog.delete.useMutation({
    onSuccess: () => {
      toast.success('调用日志删除成功');
      setDeletingLogId(null);
      setDeleteModalOpen(false);
      setLogToDelete(null);
      void utils.aiInvokeLog.list.invalidate();
      void utils.aiInvokeLog.listAll.invalidate();
    },
    onSettled: () => {
      setDeletingLogId(null);
    },
  });

  const columnOverrides = useMemo(
    () => ({
      spaceId: {
        cell: (row: AiInvokeLog) => formatSpaceLabel(row),
      },
      userId: {
        cell: (row: AiInvokeLog) => formatUserLabel(row),
      },
      providerId: {
        cell: (row: AiInvokeLog) => formatProviderLabel(row),
      },
      modelId: {
        cell: (row: AiInvokeLog) => formatModelLabel(row),
      },
      ruleId: {
        cell: (row: AiInvokeLog) => formatRuleLabel(row),
      },
      usageItems: {
        cell: (row: AiInvokeLog) => {
          const items = normalizeUsageItemsForGrid(row.usageItems);
          return <UsageItemsGrid items={items} />;
        },
      },
      status: {
        cell: (row: AiInvokeLog) => {
          const s = row.status as AiInvokeStatus | null | undefined;
          if (!s) return '—';
          const color =
            s === 'SUCCESS' ? 'success' : s === 'FAILED' ? 'danger' : ('default' as const);
          return (
            <Chip color={color} size="sm" variant="soft">
              <Chip.Label>{AI_INVOKE_STATUS_LABELS[s] ?? s}</Chip.Label>
            </Chip>
          );
        },
      },
      amount: {
        cell: (row: AiInvokeLog) => (row.amount != null ? String(row.amount) : '—'),
      },
      creditCost: {
        cell: (row: AiInvokeLog) => (row.creditCost != null ? String(row.creditCost) : '—'),
      },
    }),
    []
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<AiInvokeLog>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="ai_invoke_log"
          data={logs}
          recordCount={total}
          pageCount={pageCount}
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          columnOverrides={columnOverrides}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onPress={() => {
                  setEditingLog(null);
                  setEditorModalOpen(true);
                }}
              >
                <Plus className="size-4" stroke={1.5} />
                创建
              </Button>
              <Button variant="secondary" onPress={() => void refetch()}>
                <RefreshCw className="size-4" stroke={1.5} />
                刷新
              </Button>
            </div>
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<AiInvokeLog>>
            {({ row }) => {
              const log = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => {
                            setEditingLog(log);
                            setEditorModalOpen(true);
                          }}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete"
                          textValue="删除"
                          variant="danger"
                          onAction={() => {
                            setLogToDelete(log);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <AiInvokeLogEditorModal
        open={editorModalOpen}
        log={editingLog}
        onCancel={() => {
          setEditorModalOpen(false);
          setEditingLog(null);
        }}
        onSuccess={() => {
          setEditorModalOpen(false);
          setEditingLog(null);
          void refetch();
        }}
      />

      <AlertDialog isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除调用日志「{logToDelete?.requestId ?? logToDelete?.id}」吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setLogToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletingLogId === logToDelete?.id}
                  onPress={() => {
                    if (!logToDelete) return;
                    setDeletingLogId(logToDelete.id);
                    void deleteLogMutation.mutateAsync({ id: logToDelete.id });
                  }}
                >
                  {deletingLogId === logToDelete?.id ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
